import { Container, Divider } from "@material-ui/core";
import { Box, Card, CardContent, Grid, Typography } from "@mui/material";
import axios from "axios";
import {
  CurrencyDisplay,
  CustomTable,
  ModalError,
  ModalSuccess,
  ModalWarning,
  Page,
  PrimaryButton,
  SectionLabel,
  SkeletonComponent,
  StatusChip,
  SwitchInput,
} from "components";
import { InnoImage } from "inno-ui";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { HeaderTitle } from "layouts";
import { getTotalPage } from "lib";
import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useHistory } from "react-router";
import { useParams } from "react-router-dom";
import { hardBaseUrl } from "services/urlConstant";
import { globalStyles } from "styles";
import { formatDate, getErrors, wordCapitalize } from "utils";
import {
  chipInvoiceStatus,
  columnTableInvoiceSongDetail,
} from "./Components/ConstantInvoice";

const DetailSalesInvoicePayment = ({ userLogin }) => {
  const classes = globalStyles();
  const { id } = useParams();
  const inputRef = useRef(null);
  const history = useHistory();
  const token = localStorage.getItem("token");
  const headers = {
    Authorization: `Bearer ${token}`,
  };
  const isFinance = useSelector(
    ({ userState }) => userState?.role?.roles_for === "finance"
  );
  const [loadingPage, setLoadingPage] = useState(true);
  const [loadingButton, setLoadingButton] = useState(false);
  const [loadingPrint, setLoadingPrint] = useState(false);
  const [showDetail, setShowDetail] = useState(false);
  const [detailPdf, setDetailPdf] = useState({});
  const [detailPaymentInvoice, setDetailPaymentInvoice] = useState({});
  const [queryParams, setQueryParams] = useState({
    page: 1,
    size: 10,
  });
  const [tableTotalPage, setTableTotalPage] = useState(1);
  const userPublisher = userLogin?.publisher;
  const publisherLogo =
    userPublisher?.profile_image ||
    "https://sandbox.minio.asaba.co.id/staging-mpis/publisher/ebf4813a-c07f-4ef2-a055-4d9b8597a870.png";
  const showApproved =
    ["waiting_for_approval_payment", "waiting_for_approval"].includes(
      detailPaymentInvoice?.status
    ) && isFinance;

  const selectedStatus = chipInvoiceStatus?.find(
    ({ value }) => value === detailPaymentInvoice?.status
  );

  const approvalStatus = detailPaymentInvoice?.status;
  const isAllowedToOpenDetailPayment =
    approvalStatus === "paid" ||
    approvalStatus === "waiting_for_approval_payment" ||
    approvalStatus === "waiting_for_approval";
  const hideColumnRemainingRevenue =
    isAllowedToOpenDetailPayment || approvalStatus === "approved";

  const handleChangeQueryParams = (value, key) => {
    setQueryParams(currentState => ({
      ...currentState,
      ...(key !== "page" && { page: 1 }),
      [key]: value,
    }));
  };

  const handlePrintPDF = async () => {
    try {
      setLoadingPrint(true);
      await new Promise(resolve => setTimeout(resolve, 0));

      const doc = new jsPDF("p", "mm", "a4");

      const defaultAddress =
        "Jl. Cideng Barat No.54, RT.12/RW.1, Cideng, Kecamatan Gambir, Kota Jakarta Pusat,\nDaerah Khusus Ibukota Jakarta 10000";

      const fontSmall = 7;
      const fontNormal = 9;
      const ptToMm = pt => pt * 0.352777778;
      const lineHeightFactor = 1.15;
      const textSpacing = 8; // Spasi antar baris teks (Fee, Tax, Total Fee)
      const lineSpacing = 4; // Spasi untuk garis pemisah (Divider)

      doc.setFontSize(fontSmall);
      const addressText = userPublisher?.address || defaultAddress;
      const wrappedAddress = doc.splitTextToSize(addressText, 100);
      const addressLineHeight = ptToMm(fontSmall) * lineHeightFactor;
      const lastYAddress = 8 + wrappedAddress.length * addressLineHeight;

      doc.setFontSize(fontNormal);
      const wrappedNote = doc.splitTextToSize(
        detailPaymentInvoice?.notes || "",
        100
      );
      const noteLineHeight = ptToMm(fontNormal) * lineHeightFactor;
      const lastYNote = 68 + wrappedNote.length * noteLineHeight;

      const headerBottom = Math.ceil(
        Math.max(lastYAddress + 10, lastYNote + 6, 80)
      );

      // Fungsi header biar bisa dipakai ulang
      const drawHeader = doc => {
        if (userPublisher?.profile_image) {
          try {
            doc.addImage(userPublisher.profile_image, "PNG", 8, 4, 25, 15);
          } catch (err) {
            console.warn("addImage gagal:", err);
          }
        }

        doc.setFontSize(fontSmall);
        doc.setTextColor("#6F6F84");
        doc.text(wrappedAddress, 110, 8);

        // Divider
        const lastY = 8 + wrappedAddress.length * addressLineHeight;
        doc.setDrawColor(200);
        doc.line(8, lastY + 10, 200, lastY + 10);

        // Invoice Info
        doc.setTextColor("#263238");
        doc.setFontSize(fontNormal);

        doc.setFont("helvetica", "bold");
        doc.text("Payment To", 8, 40);
        doc.setFont("helvetica", "normal");
        doc.text(detailPaymentInvoice.composer_name || "-", 8, 46);

        doc.setFont("helvetica", "bold");
        doc.text("Address", 8, 52);
        doc.setFont("helvetica", "normal");
        const wrappedComposerAddress = doc.splitTextToSize(
          detailPaymentInvoice?.composer_address || "-",
          100
        );
        doc.text(wrappedComposerAddress, 8, 58);

        doc.setFont("helvetica", "bold");
        doc.text("Payment Number", 130, 40);
        doc.setFont("helvetica", "normal");
        doc.text(detailPaymentInvoice.payment_number || "-", 130, 46);

        doc.setFont("helvetica", "bold");
        doc.text("Payment Date", 130, 52);
        doc.setFont("helvetica", "normal");
        doc.text(formatDate(detailPaymentInvoice.payment_date) || "-", 130, 58);

        doc.setFont("helvetica", "bold");
        doc.text("Note", 8, 68);
        doc.setFont("helvetica", "normal");
        doc.text(wrappedNote, 8, 74);

        // Nomor halaman sengaja dihapus di sini
      };

      let finalY = headerBottom;

      if (showDetail) {
        const data = detailPdf?.map(song => {
          return columnTableInvoiceSongDetail({
            hideColumnRemainingRevenue,
            hideColumnPublisherRevenue: true,
          })?.map(col => {
            switch (col.headerAlign) {
              case "right":
                return `Rp${Number(song?.[col.name] || 0).toLocaleString(
                  "id-ID",
                  {
                    minimumFractionDigits: 2,
                    maximumFractionDigits: 2,
                  }
                )}`;

              default:
                return song?.[col.name] || "-";
            }
          });
        });

        autoTable(doc, {
          head: [
            columnTableInvoiceSongDetail({
              hideColumnRemainingRevenue: true,
              hideColumnPublisherRevenue: true,
            })?.map(col => col?.title),
          ],
          body: data,
          startY: headerBottom,
          margin: { top: headerBottom },
          styles: { fontSize: 7, cellPadding: 4, valign: "middle" },
          headStyles: {
            fillColor: [230, 230, 230],
            textColor: 20,
            fontStyle: "bold",
          },
          didDrawPage: () => {
            drawHeader(doc); // Tidak perlu kirim pageNumber lagi
          },
        });

        finalY = doc.lastAutoTable.finalY || headerBottom;
        finalY += 10;
      } else {
        // Kalau tabel disembunyikan, tetap gambar header manual
        drawHeader(doc);
        finalY = headerBottom + 10;
      }

      // =========================================================
      // FEE & TAXES (DINAMIS)
      // =========================================================

      // 1. Baris Fee
      doc.setFont("helvetica", "normal");
      doc.setFontSize(10);
      doc.text("Fee", 8, finalY);
      doc.text(
        `Rp${Number(detailPaymentInvoice?.fee).toLocaleString("id-ID", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        200,
        finalY,
        {
          align: "right",
        }
      );

      // Pindah finalY ke bawah baris Fee
      finalY += lineSpacing;
      doc.setDrawColor(200);
      doc.line(8, finalY, 200, finalY);
      finalY += textSpacing - lineSpacing; // Naikkan untuk baris teks berikutnya

      // 2. Baris Taxes (Dinamis)
      const taxes = detailPaymentInvoice.selected_taxes;
      if (taxes && taxes.length > 0) {
        taxes.forEach(item => {
          // Tambahkan teks tax
          doc.setFont("helvetica", "normal");
          doc.setFontSize(10);
          doc.text(item.code, 8, finalY);
          // Menggunakan tanda minus (-) karena biasanya potongan pajak (Tax Withheld)
          doc.text(
            `${item?.method === "deduction" ? "-" : ""}${Math.abs(
              Number(item?.value)
            ).toLocaleString("id-ID", {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`,
            200,
            finalY,
            {
              align: "right",
            }
          );

          // Pindah finalY ke bawah baris Tax
          finalY += lineSpacing;
          doc.setDrawColor(200);
          doc.line(8, finalY, 200, finalY);
          finalY += textSpacing - lineSpacing; // Naikkan untuk baris teks berikutnya
        });
      }

      // 3. Baris Total Fee
      doc.setFont("helvetica", "bold");
      doc.text("Total Fee", 8, finalY); // finalY sudah diposisikan dengan benar di bawah semua tax
      doc.text(
        `Rp${Number(detailPaymentInvoice?.total_fee).toLocaleString("id-ID", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        })}`,
        200,
        finalY,
        { align: "right" }
      );

      // =========================================================
      // PENOMORAN HALAMAN AKHIR (Mencegah Bentrokan Footer)
      // =========================================================
      const pageCount = doc.internal.getNumberOfPages();
      for (let i = 1; i <= pageCount; i++) {
        doc.setPage(i);
        doc.setFont("helvetica", "normal");
        doc.setFontSize(8);
        doc.setTextColor("#6F6F84");

        // Posisikan nomor halaman 4mm dari bawah
        const pageY = doc.internal.pageSize.getHeight() - 4;

        doc.text(
          `Page ${i} of ${pageCount}`,
          doc.internal.pageSize.getWidth() - 8,
          pageY,
          { align: "right" }
        );
      }

      // Pastikan kita kembali ke halaman terakhir sebelum menyimpan
      doc.setPage(pageCount);

      doc.save(
        `Download payment invoice ${detailPaymentInvoice?.payment_number ||
          "composer"}.pdf`
      );
    } finally {
      setLoadingPrint(false);
    }
  };
  const handleConfirm = status => {
    const approved = status === "approve";
    const approvalLabel = approved ? "approve" : "reject";
    ModalWarning(
      `Are you sure to ${approvalLabel} this data ?`,
      `Confirm ${wordCapitalize(approvalLabel)} Payment`
    ).then(res => res?.isConfirmed && handleApproval(status));
  };

  const getDataDetail = async () => {
    try {
      setLoadingPage(true);
      const response = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/payment/${id}`,
        {
          headers,
          params: queryParams,
        }
      );
      const data = response?.data?.data || {};
      const pageCount = getTotalPage(data?.songs?.total, queryParams?.size);
      setTableTotalPage(pageCount || 1);
      setDetailPaymentInvoice(data);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const getDataDetailPdf = async () => {
    try {
      setLoadingPage(true);
      const response = await axios.get(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/payment/${id}`,
        {
          headers,
          params: { page: 1 },
        }
      );
      const { data } = response?.data || {};
      setDetailPdf(data.sales_invoices?.data?.sales_invoices);
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingPage(false);
    }
  };
  const handleApproval = async type => {
    const approved = type === "approve";
    const approvalLabel = approved ? "approved" : "rejected";
    try {
      setLoadingButton(true);
      await axios.put(
        `${hardBaseUrl}/publisher/transaction/v2/composer-invoice/payment/${approvalLabel}/${id}`,
        {},
        { headers }
      );
      ModalSuccess(`Succecfully ${type} payment`).then(() =>
        approved
          ? getDataDetail()
          : history.replace(`/admin/composer-invoice/${id}`)
      );
    } catch (error) {
      ModalError(getErrors(error?.response));
    } finally {
      setLoadingButton(false);
    }
  };

  useEffect(() => {
    getDataDetail();
  }, [queryParams]);

  useEffect(() => {
    getDataDetailPdf();
  }, []);

  return (
    <Page className={classes.root} title="Payment Detail">
      {loadingPage ? (
        <SkeletonComponent variant="wave" />
      ) : (
        <Container maxWidth={false}>
          <HeaderTitle
            title="Payment Detail"
            breadcrumbData={breadcrumbData({ id })}
            backButton
          />
          <Divider className={classes.divider} />
          <Grid container justifyContent="space-between">
            <Grid item>
              <SectionLabel
                title="Payment Detail"
                subTitle="This is detail credit note information, in payment transaction"
              />
            </Grid>
            <Grid item>
              <StatusChip
                label={selectedStatus?.name}
                type={selectedStatus?.color}
              />
            </Grid>
          </Grid>
          <Box
            border="1px solid #9AA2B1"
            padding="24px"
            borderRadius="8px"
            my="16px"
            ref={inputRef}
          >
            <Grid container alignItems="center">
              <Grid item xs={12} md={6}>
                <InnoImage
                  src={publisherLogo}
                  alt="img"
                  fill={true}
                  styleImage={{
                    maxWidth: "155.52px",
                    width: "100%",
                    height: "100%",
                    maxHeight: "50px",
                    objectFit: "contain",
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <Typography textAlign="right" fontSize={14} color="#6F6F84">
                  {userPublisher?.address ||
                    "Jl. Setia Budi, RT 05 RW 05, Karet Kuningan, South Jakarta"}
                </Typography>
              </Grid>
            </Grid>
            <Divider className={classes.divider} />
            <Grid container spacing={2}>
              {mockFieldForm?.map(({ title, key }, index) => (
                <Grid item xs={12} md={key === "notes" ? 12 : 6} key={index}>
                  <Typography
                    className={classes.fieldTitle}
                    textAlign={index % 2 !== 0 && "right"}
                  >
                    {title}
                  </Typography>
                  <Typography
                    className={classes.fieldValue}
                    textAlign={index % 2 !== 0 && "right"}
                  >
                    {key === "payment_date"
                      ? formatDate(detailPaymentInvoice?.[key])
                      : detailPaymentInvoice?.[key]}
                  </Typography>
                </Grid>
              ))}
              <Grid item xs={12} className="hideWhenPrint">
                <SwitchInput
                  checked={showDetail}
                  onChange={() => setShowDetail(show => !show)}
                  label="Show Source Detail"
                />
              </Grid>
              {showDetail && (
                <Grid item xs={12}>
                  <CustomTable
                    columnTable={columnTableInvoiceSongDetail({
                      hideColumnRemainingRevenue: true,
                    })}
                    data={
                      detailPaymentInvoice?.sales_invoices?.data
                        ?.sales_invoices || []
                    }
                    page={queryParams?.page}
                    pageSize={queryParams?.size}
                    pageCount={tableTotalPage}
                    handleChangePage={(_, page) =>
                      handleChangeQueryParams(page, "page")
                    }
                    handleChangePageSize={e =>
                      handleChangeQueryParams(e?.target?.value, "size")
                    }
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <Box marginTop="10px" gap={2}>
                  <Card className={classes?.totalWrapper}>
                    <CardContent>
                      <Grid
                        container
                        justifyContent="right"
                        mt="16px"
                        spacing={2}
                      >
                        <Grid item xs={12}>
                          <Box
                            justifyContent="space-between"
                            alignItems="center"
                            display="flex"
                          >
                            <Typography className={classes?.fieldTitle}>
                              Fee
                            </Typography>
                            <Typography
                              textAlign="right"
                              className={classes?.fieldTitle}
                            >
                              <CurrencyDisplay
                                value={detailPaymentInvoice?.fee}
                                prefix={"Rp "}
                                decimalScale={2}
                              />
                            </Typography>
                          </Box>
                        </Grid>
                        {(detailPaymentInvoice?.selected_taxes || [])?.map(
                          tax => (
                            <Grid item xs={12} key={tax?.id}>
                              <Box
                                justifyContent="space-between"
                                alignItems="center"
                                display="flex"
                              >
                                <Typography className={classes?.fieldTitle}>
                                  {`${tax?.code}`}
                                </Typography>
                                <Typography
                                  textAlign="right"
                                  className={classes?.fieldTitle}
                                >
                                  <CurrencyDisplay
                                    value={tax?.value}
                                    decimalScale={2}
                                    prefix="Rp"
                                  />
                                </Typography>
                              </Box>
                            </Grid>
                          )
                        )}
                        <Grid item xs={12}>
                          <Divider className={classes.divider} />
                          <Box
                            justifyContent="space-between"
                            alignItems="center"
                            display="flex"
                          >
                            <Typography>Total Fee</Typography>
                            <Typography
                              textAlign="right"
                              className={classes?.fieldValue}
                            >
                              <CurrencyDisplay
                                value={detailPaymentInvoice?.total_fee}
                                prefix={"Rp "}
                                decimalScale={2}
                              />
                            </Typography>
                          </Box>
                        </Grid>
                      </Grid>
                    </CardContent>
                  </Card>
                </Box>
              </Grid>
            </Grid>
          </Box>
          <Grid container justifyContent="right" gap="8px">
            <PrimaryButton
              onClick={handlePrintPDF}
              disabled={loadingPrint || loadingButton || loadingPage}
              loading={loadingPrint || loadingButton || loadingPage}
              label={
                loadingPrint || loadingButton ? "Generating PDF..." : "Print"
              }
            />
            {showApproved &&
              approvalButtons?.map(({ label, value }, index) => (
                <PrimaryButton
                  key={index}
                  label={loadingButton ? "Processing..." : label}
                  disabled={loadingButton || loadingPrint || loadingPage}
                  loading={loadingButton || loadingPrint || loadingPage}
                  onClick={() => handleConfirm(value)}
                  color={value === "rejected" && "#FF3E00"}
                />
              ))}
          </Grid>
        </Container>
      )}
    </Page>
  );
};

const breadcrumbData = ({ id }) => [
  {
    label: "Home",
    link: "/admin/dashboard",
  },
  {
    label: "Transaction",
    link: "/admin/composer-invoice",
  },
  {
    label: "Composer Invoice",
    link: "/admin/composer-invoice",
  },
  {
    label: "Composer Invoice Detail",
    link: `/admin/composer-invoice/${id}`,
  },
  {
    label: "Payment Detail",
    active: true,
  },
];
const mockFieldForm = [
  { title: "Payment To", key: "composer_name" },
  { title: "Payment Number", key: "payment_number" },
  { title: "Address", key: "composer_address" },
  { title: "Payment Date", key: "payment_date" },
  { title: "Note", key: "notes" },
];
const approvalButtons = [
  { label: "Reject", value: "rejected" },
  { label: "Approve", value: "approve" },
];

export default DetailSalesInvoicePayment;
